import React, { useState, useEffect } from 'react';
import { useAppState } from '../contexts/AppStateContext';
import api from '../utils/axiosConfig';

const EditEvent = ({ event, onUpdate }) => {
    const { currentOrganization } = useAppState();
    const [customDomains, setCustomDomains] = useState([]);
    const [formData, setFormData] = useState(event || {});
    const [logoPreview, setLogoPreview] = useState(null);
    const [ticketImagePreview, setTicketImagePreview] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fileData, setFileData] = useState({
        logo: null,
        ticket_image: null
    });
    const [changedFields, setChangedFields] = useState({});
    
    // Initialize form data when event prop changes
    useEffect(() => {
        if (event) {
            setFormData(event);
            setChangedFields({}); // Reset changed fields when event changes
            if (event.logo && typeof event.logo === 'string') {
                setLogoPreview(event.logo);
            }
            if (event.ticket_image && typeof event.ticket_image === 'string') {
                setTicketImagePreview(event.ticket_image);
            }
        }
    }, [event]);

    // Effect to auto-enable auto_approve when is_paid is true
    useEffect(() => {
        if (formData.is_paid) {
            // Only update if auto_approve is not already true
            if (!formData.auto_approve) {
                setFormData(prev => ({ ...prev, auto_approve: true }));
                
                // Update changed fields to include auto_approve if it differs from original
                if (event && !event.auto_approve) {
                    setChangedFields(prev => ({ ...prev, auto_approve: true }));
                }
            }
        }
    }, [formData.is_paid, event]);

    // Fetch custom domains on component mount
    useEffect(() => {
        const fetchCustomDomains = async () => {
          try {
            const response = await api.get(`/organizations/${currentOrganization.id}/custom-domains/`);
            setCustomDomains(response.data);
          } catch (err) {
            console.error('Error fetching custom domains:', err);
          }
        };
    
        if (currentOrganization?.id) {
          fetchCustomDomains();
        }
    }, [currentOrganization]);
      
    const handleChange = (e) => {
        const { name, value, type, checked, files } = e.target;

        if (type === 'checkbox') {
            const newValue = checked;
            
            if (name === 'is_paid') {
                // If is_paid is being checked, also set auto_approve to true
                if (newValue === true) {
                    setFormData(prev => ({ 
                        ...prev, 
                        [name]: newValue,
                        auto_approve: true 
                    }));
                    
                    // Track changes for both fields if they differ from original
                    const updatedChangedFields = { ...changedFields, [name]: newValue };
                    if (event.auto_approve !== true) {
                        updatedChangedFields.auto_approve = true;
                    }
                    setChangedFields(updatedChangedFields);
                } else {
                    // If is_paid is being unchecked, just update is_paid normally
                    setFormData(prev => ({ ...prev, [name]: newValue }));
                    
                    // Only track as changed if the value is different from the original
                    if (event[name] !== newValue) {
                        setChangedFields(prev => ({ ...prev, [name]: newValue }));
                    } else {
                        // If value is back to original, remove from changed fields
                        setChangedFields(prev => {
                            const updated = { ...prev };
                            delete updated[name];
                            return updated;
                        });
                    }
                }
            } else if (name === 'auto_approve') {
                // If trying to uncheck auto_approve while is_paid is true, prevent it
                if (formData.is_paid && !newValue) {
                    // Don't allow unchecking auto_approve when is_paid is true
                    return;
                }
                
                // Otherwise update normally
                setFormData(prev => ({ ...prev, [name]: newValue }));
                
                // Only track as changed if the value is different from the original
                if (event[name] !== newValue) {
                    setChangedFields(prev => ({ ...prev, [name]: newValue }));
                } else {
                    // If value is back to original, remove from changed fields
                    setChangedFields(prev => {
                        const updated = { ...prev };
                        delete updated[name];
                        return updated;
                    });
                }
            } else {
                // Handle other checkboxes normally
                setFormData(prev => ({ ...prev, [name]: newValue }));
                
                // Only track as changed if the value is different from the original
                if (event[name] !== newValue) {
                    setChangedFields(prev => ({ ...prev, [name]: newValue }));
                } else {
                    // If value is back to original, remove from changed fields
                    setChangedFields(prev => {
                        const updated = { ...prev };
                        delete updated[name];
                        return updated;
                    });
                }
            }
        } else if (type === 'file') {
            // Store file objects separately and always consider files as changed
            if (files[0]) {
                setFileData(prev => ({ ...prev, [name]: files[0] }));
                setChangedFields(prev => ({ ...prev, [name]: 'file_changed' }));
                
                // Create preview for image files
                if (name === 'logo') {
                    const newLogoURL = URL.createObjectURL(files[0]);
                    setLogoPreview(newLogoURL);
                } else if (name === 'ticket_image') {
                    const newTicketURL = URL.createObjectURL(files[0]);
                    setTicketImagePreview(newTicketURL);
                }
            }
        } else {
            // Special handling for custom_domain field
            if (name === 'custom_domain') {
                const newValue = value;
                // Update form data with new value
                if (newValue === '') {
                    // If empty, set to null or empty string based on your backend needs
                    setFormData(prev => ({ ...prev, [name]: null }));
                } else {
                    // For an actual selection, try to find the corresponding domain object
                    const selectedDomain = customDomains.find(domain => domain.id === newValue);
                    setFormData(prev => ({ ...prev, [name]: selectedDomain || newValue }));
                }
                
                // Compare with original value to track changes
                const originalDomainId = event.custom_domain?.id || '';
                if (originalDomainId !== newValue) {
                    setChangedFields(prev => ({ ...prev, [name]: newValue }));
                } else {
                    // If value is back to original, remove from changed fields
                    setChangedFields(prev => {
                        const updated = { ...prev };
                        delete updated[name];
                        return updated;
                    });
                }
            } else {
                // Handle other fields normally
                const newValue = value;
                setFormData(prev => ({ ...prev, [name]: newValue }));
                
                // Only track as changed if the value is different from the original
                if (event[name] !== newValue) {
                    setChangedFields(prev => ({ ...prev, [name]: newValue }));
                } else {
                    // If value is back to original, remove from changed fields
                    setChangedFields(prev => {
                        const updated = { ...prev };
                        delete updated[name];
                        return updated;
                    });
                }
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        
        try {
            // Check if there are any changes
            if (Object.keys(changedFields).length === 0 && !fileData.logo && !fileData.ticket_image) {
                console.log('No changes detected');
                setIsSubmitting(false);
                return;
            }
            
            // Create a FormData object for sending files
            const submitData = new FormData();
            
            // Add only changed fields to FormData (excluding file fields)
            Object.keys(changedFields).forEach(key => {
                if (key !== 'logo' && key !== 'ticket_image') {
                    // Special handling for custom_domain
                    if (key === 'custom_domain') {
                        const customDomainValue = changedFields[key];
                        if (customDomainValue === '' || customDomainValue === null) {
                            // Explicitly send empty string to clear the custom domain
                            submitData.append('custom_domain', '');
                        } else {
                            // Otherwise send the ID as is
                            submitData.append('custom_domain', customDomainValue);
                        }
                    } else {
                        submitData.append(key, changedFields[key]);
                    }
                }
            });
            
            // Add file fields from fileData if they exist
            if (fileData.logo) {
                submitData.append('logo', fileData.logo);
            }
            
            if (fileData.ticket_image) {
                submitData.append('ticket_image', fileData.ticket_image);
            }
            
            // Only send data if there are changes
            if ([...submitData.keys()].length > 0) {
                // Add the ID to ensure the API knows which record to update
                if (!submitData.has('id') && event.id) {
                    submitData.append('id', event.id);
                }
                
                // For debugging
                console.log('Submitting changes:', Object.fromEntries(submitData.entries()));
                
                // Pass the FormData object with only changed fields to onUpdate
                await onUpdate(submitData);
            } else {
                console.log('No changes to send');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Clean up object URLs when component unmounts
    useEffect(() => {
        return () => {
            // Only revoke URLs that were created by this component, not ones passed in
            if (logoPreview && !event.logo) URL.revokeObjectURL(logoPreview);
            if (ticketImagePreview && !event.ticket_image) URL.revokeObjectURL(ticketImagePreview);
        };
    }, [logoPreview, ticketImagePreview, event]);

    // Form groups as specified
    const formGroups = [
        {
            title: "General Info",
            fields: ["name", "custom_domain", "date", "logo", "ticket_image"],
        },
        {
            title: "Payment and Registration",
            fields: [
                "auto_approve",
                "is_paid",
                "form_structure",
                "registration_start_date",
                "registration_end_date",
                "allow_multiple_tickets_purchase",
                "max_tickets_per_customer",
            ],
        },
        {
            title: "Communication",
            fields: [
                "submission_approved_email",
                "submission_rejected_email",
                "submission_received_email",
                "event_reminder_email",
            ],
        }
    ];

    // Render form field based on field name
    const renderField = (fieldName) => {
        switch (fieldName) {
            case 'name':
                return (
                    <div className="mb-4">
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700">Event Name</label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name || ''}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            required
                        />
                    </div>
                );

            case 'custom_domain':
                return (
                    <div className="mb-4">
                        <label htmlFor="custom_domain" className="block text-sm font-medium text-gray-700">Custom Domain</label>
                        <select
                            id="custom_domain"
                            name="custom_domain"
                            value={formData.custom_domain?.id || ''}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-indigo-500 focus:ring sm:text-sm"
                        >
                            <option value="">None</option>
                            {customDomains.map(domain => (
                                <option key={domain.id} value={domain.id}>
                                    {domain.domain}
                                </option>
                            ))}
                        </select>
                        {customDomains.length === 0 && (
                            <p className="text-gray-500 text-sm mt-1">
                                No custom domains available. Please create one in the organization settings.
                            </p>
                        )}
                    </div>
                );

            case 'date':
                return (
                    <div className="mb-4">
                        <label htmlFor="date" className="block text-sm font-medium text-gray-700">Event Date</label>
                        <input
                            type="datetime-local"
                            id="date"
                            name="date"
                            value={formData.date ? formData.date.substring(0, 16) : ''}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            required
                        />
                    </div>
                );

            case 'logo':
                return (
                    <div className="mb-4">
                        <label htmlFor="logo" className="block text-sm font-medium text-gray-700">Event Logo</label>
                        <input
                            type="file"
                            id="logo"
                            name="logo"
                            onChange={handleChange}
                            className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                            accept="image/*"
                        />
                        {logoPreview && (
                            <div className="mt-2">
                                <img
                                    src={logoPreview}
                                    alt="Logo preview"
                                    className="h-20 w-auto object-contain"
                                />
                            </div>
                        )}
                    </div>
                );

            case 'ticket_image':
                return (
                    <div className="mb-4">
                        <label htmlFor="ticket_image" className="block text-sm font-medium text-gray-700">Ticket Image</label>
                        <input
                            type="file"
                            id="ticket_image"
                            name="ticket_image"
                            onChange={handleChange}
                            className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                            accept="image/*"
                        />
                        {ticketImagePreview && (
                            <div className="mt-2">
                                <img
                                    src={ticketImagePreview}
                                    alt="Ticket preview"
                                    className="h-20 w-auto object-contain"
                                />
                            </div>
                        )}
                    </div>
                );
            case 'auto_approve':
                return (
                    <div className="mb-4 flex items-start">
                        <div className="flex h-5 items-center">
                            <input
                                type="checkbox"
                                id="auto_approve"
                                name="auto_approve"
                                checked={formData.auto_approve || false}
                                onChange={handleChange}
                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                disabled={formData.is_paid} // Disable when is_paid is true
                            />
                        </div>
                        <div className="ml-3 text-sm">
                            <label htmlFor="auto_approve" className="font-medium text-gray-700">
                                Auto Approve Registrations
                                {formData.is_paid && (
                                    <span className="ml-2 text-xs italic text-indigo-600">
                                        (Required for paid events)
                                    </span>
                                )}
                            </label>
                        </div>
                    </div>
                );

            case 'is_paid':
                return (
                    <div className="mb-4 flex items-start">
                        <div className="flex h-5 items-center">
                            <input
                                type="checkbox"
                                id="is_paid"
                                name="is_paid"
                                checked={formData.is_paid || false}
                                onChange={handleChange}
                                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                        </div>
                        <div className="ml-3 text-sm">
                            <label htmlFor="is_paid" className="font-medium text-gray-700">
                                Paid Event
                                <span className="ml-2 text-xs italic text-gray-500">
                                    (Will automatically enable auto-approval)
                                </span>
                            </label>
                        </div>
                    </div>
                );

            case 'registration_start_date':
                return (
                    <div className="mb-4">
                        <label htmlFor="registration_start_date" className="block text-sm font-medium text-gray-700">Registration Start Date</label>
                        <input
                            type="datetime-local"
                            id="registration_start_date"
                            name="registration_start_date"
                            value={formData.registration_start_date ? formData.registration_start_date.substring(0, 16) : ''}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            required
                        />
                    </div>
                );

            case 'registration_end_date':
                return (
                    <div className="mb-4">
                        <label htmlFor="registration_end_date" className="block text-sm font-medium text-gray-700">Registration End Date</label>
                        <input
                            type="datetime-local"
                            id="registration_end_date"
                            name="registration_end_date"
                            value={formData.registration_end_date ? formData.registration_end_date.substring(0, 16) : ''}
                            onChange={handleChange}
                            className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            required
                        />
                    </div>
                );

            case 'allow_multiple_tickets_purchase':
                return (
                    <div className="mb-4 flex items-start">
                        <div className="flex h-5 items-center">
                            <input
                                type="checkbox"
                                id="allow_multiple_tickets_purchase"
                                name="allow_multiple_tickets_purchase"
                                checked={formData.allow_multiple_tickets_purchase || false}
                                onChange={handleChange}
                                className="h-4 w-4 rounded border-gray-300 p-2 text-indigo-600 focus:ring-indigo-500"
                            />
                        </div>
                        <div className="ml-3 text-sm">
                            <label htmlFor="allow_multiple_tickets_purchase" className="font-medium text-gray-700">Allow Multiple Tickets Purchase</label>
                        </div>
                    </div>
                );

            case 'max_tickets_per_customer':
                return (
                    <div className="mb-4">
                        <label htmlFor="max_tickets_per_customer" className="block text-sm font-medium text-gray-700">
                            Max Tickets Per Customer
                            <span className="ml-2 text-xs italic text-gray-500">
                                (Counted by email. Leave blank for no limit)
                            </span>
                        </label>
                        <input
                            type="number"
                            min="1"
                            step="1"
                            id="max_tickets_per_customer"
                            name="max_tickets_per_customer"
                            value={formData.max_tickets_per_customer ?? ''}
                            onChange={handleChange}
                            placeholder="Unlimited"
                            className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                    </div>
                );

            case 'submission_approved_email':
            case 'submission_rejected_email':
            case 'submission_received_email':
            case 'event_reminder_email':
                const emailLabels = {
                    'submission_approved_email': 'Submission Approved Email Template',
                    'submission_rejected_email': 'Submission Rejected Email Template',
                    'submission_received_email': 'Submission Received Email Template',
                    'event_reminder_email': 'Event Reminder Email Template'
                };

                return (
                    <div className="mb-4">
                        <label htmlFor={fieldName} className="block text-sm font-medium text-gray-700">{emailLabels[fieldName]}</label>
                        <textarea
                            id={fieldName}
                            name={fieldName}
                            value={formData[fieldName] || ''}
                            onChange={handleChange}
                            rows={4}
                            className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className='mb-6'>
            <form onSubmit={handleSubmit}>
                {formGroups.map((group, groupIndex) => (
                    <div key={groupIndex} className="collapse bg-base-100 border-gray-500 border mb-6">
                        <input type="checkbox" defaultChecked />
                        <div className="collapse-title font-semibold">{group.title}</div>
                        <div className="collapse-content text-sm">
                            <div className="mb-8">
                                <h3 className="text-lg font-medium text-gray-900 mb-4 pb-2 border-b">{group.title}</h3>
                                <div className="space-y-4">
                                    {group.fields.map(field => renderField(field))}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}

                <div className="flex justify-end mt-6">
                    <button
                        type="button"
                        className="mr-3 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        onClick={() => onUpdate(event)} // Cancel by passing the original event back
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <span className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Saving...
                            </span>
                        ) : (
                            'Save Changes'
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default EditEvent;